import { useEffect, useState } from "react";
import { adminGetSiteContent, adminPutSiteContent, adminUploadImage, adminUploadVideo } from "../api/admin";
import BackblazeUploadButton from "../components/BackblazeUploadButton";
import { adminTheme } from "../styles/adminTheme";

const dtLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AdminLandingTab = ({ isDark, setToast, baseText, mutedText, cardBase }) => {
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await adminGetSiteContent();
        if (!cancelled && res.success) setSite(res.data);
      } catch (err) {
        if (!cancelled) setToast?.(err?.response?.data?.message || "Failed to load landing content");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setToast]);

  const theme = adminTheme(isDark);
  const inputCls = `mt-1.5 w-full rounded-xl border px-3 py-2 text-sm outline-none ${theme.inputBase}`;
  const labelCls = `block text-xs font-medium ${mutedText}`;

  if (loading || !site) {
    return <div className={`${cardBase} rounded-2xl p-10 text-center text-sm ${mutedText}`}>Loading landing content…</div>;
  }

  const save = async (e) => {
    e.preventDefault();
    try {
      await adminPutSiteContent(site);
      setToast?.("Landing page saved");
    } catch (err) {
      setToast?.(err?.response?.data?.message || "Save failed");
    }
  };

  return (
    <form onSubmit={save} className="space-y-10 max-w-4xl">
      <div className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4`}>
        <h2 className={`text-base font-semibold tracking-tight ${baseText}`}>Hero & about</h2>
        <label className={labelCls}>
          Hero title
          <input className={inputCls} value={site.heroTitle || ""} onChange={(e) => setSite((s) => ({ ...s, heroTitle: e.target.value }))} />
        </label>
        <label className={labelCls}>
          Hero subtitle
          <textarea rows={2} className={inputCls} value={site.heroSubtitle || ""} onChange={(e) => setSite((s) => ({ ...s, heroSubtitle: e.target.value }))} />
        </label>
        <label className={labelCls}>
          About section title
          <input className={inputCls} value={site.aboutTitle || ""} onChange={(e) => setSite((s) => ({ ...s, aboutTitle: e.target.value }))} />
        </label>
        <label className={labelCls}>
          Mission / about text
          <textarea rows={5} className={inputCls} value={site.aboutMission || ""} onChange={(e) => setSite((s) => ({ ...s, aboutMission: e.target.value }))} />
        </label>
      </div>

      <div className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4`}>
        <h2 className={`text-base font-semibold tracking-tight ${baseText}`}>Video & countdowns</h2>
        <label className={labelCls}>
          Featured video — YouTube/Vimeo URL, or upload MP4/WebM to Backblaze
          <div className="mt-1.5 flex flex-wrap gap-2 items-center">
            <input
              className={`${inputCls} flex-1 min-w-[200px]`}
              value={site.landingVideoUrl || ""}
              onChange={(e) => setSite((s) => ({ ...s, landingVideoUrl: e.target.value }))}
              placeholder="https://…"
            />
            <BackblazeUploadButton
              accept="video/*"
              uploadFn={adminUploadVideo}
              folder="cms/landing"
              onUploaded={(url) => setSite((s) => ({ ...s, landingVideoUrl: url }))}
              onError={(m) => setToast(m)}
              label="Upload video (B2)"
            />
          </div>
          <p className={`text-xs mt-1 ${mutedText}`}>Uploaded files go to your B2 videos bucket; public URL is filled in automatically.</p>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className={labelCls}>
            {site.waitlistLabel || "Waitlist"} target
            <input
              type="datetime-local"
              className={inputCls}
              value={dtLocal(site.waitlistTargetAt)}
              onChange={(e) =>
                setSite((s) => ({
                  ...s,
                  waitlistTargetAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                }))
              }
            />
          </label>
          <label className={labelCls}>
            Waitlist label
            <input className={inputCls} value={site.waitlistLabel || ""} onChange={(e) => setSite((s) => ({ ...s, waitlistLabel: e.target.value }))} />
          </label>
          <label className={labelCls}>
            {site.officialLaunchLabel || "Launch"} target
            <input
              type="datetime-local"
              className={inputCls}
              value={dtLocal(site.officialLaunchTargetAt)}
              onChange={(e) =>
                setSite((s) => ({
                  ...s,
                  officialLaunchTargetAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                }))
              }
            />
          </label>
          <label className={labelCls}>
            Official launch label
            <input className={inputCls} value={site.officialLaunchLabel || ""} onChange={(e) => setSite((s) => ({ ...s, officialLaunchLabel: e.target.value }))} />
          </label>
        </div>
        <label className={labelCls}>
          “Create Mint Space” button label
          <input
            className={inputCls}
            value={site.createMintSpaceButtonText || ""}
            onChange={(e) => setSite((s) => ({ ...s, createMintSpaceButtonText: e.target.value }))}
          />
        </label>
      </div>

      <div className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4`}>
        <div className="flex items-center justify-between gap-4">
          <h2 className={`text-base font-semibold tracking-tight ${baseText}`}>Business models</h2>
          <button
            type="button"
            onClick={() =>
              setSite((s) => ({
                ...s,
                businessModels: [
                  ...(s.businessModels || []),
                  { title: "", subtitle: "", logoUrl: "", order: (s.businessModels || []).length, isAgency: false },
                ],
              }))
            }
            className="text-sm font-semibold text-mint"
          >
            + Add row
          </button>
        </div>
        <label className={labelCls}>
          Section title
          <input
            className={inputCls}
            value={site.businessModelsSectionTitle || ""}
            onChange={(e) => setSite((s) => ({ ...s, businessModelsSectionTitle: e.target.value }))}
          />
        </label>
        {(site.businessModels || []).map((row, i) => (
          <div key={i} className={`p-4 rounded-xl ${theme.inset}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className={inputCls}
                placeholder="Title"
                value={row.title}
                onChange={(e) => {
                  const next = [...(site.businessModels || [])];
                  next[i] = { ...next[i], title: e.target.value };
                  setSite((s) => ({ ...s, businessModels: next }));
                }}
              />
              <input
                className={inputCls}
                placeholder="Subtitle (optional)"
                value={row.subtitle}
                onChange={(e) => {
                  const next = [...(site.businessModels || [])];
                  next[i] = { ...next[i], subtitle: e.target.value };
                  setSite((s) => ({ ...s, businessModels: next }));
                }}
              />
            </div>
            <label className={`${labelCls} mt-3`}>
              Logo
              <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                <input
                  className={`${inputCls} flex-1 min-w-[160px]`}
                  placeholder="Logo URL (or upload to Backblaze)"
                  value={row.logoUrl}
                  onChange={(e) => {
                    const next = [...(site.businessModels || [])];
                    next[i] = { ...next[i], logoUrl: e.target.value };
                    setSite((s) => ({ ...s, businessModels: next }));
                  }}
                />
                <BackblazeUploadButton
                  accept="image/*"
                  uploadFn={adminUploadImage}
                  folder="cms/landing/models"
                  onUploaded={(url) => {
                    const next = [...(site.businessModels || [])];
                    next[i] = { ...next[i], logoUrl: url };
                    setSite((s) => ({ ...s, businessModels: next }));
                  }}
                  onError={(m) => setToast(m)}
                  label="Upload logo"
                />
              </div>
            </label>
            <button
              type="button"
              className="mt-2 text-xs text-rose-400"
              onClick={() => {
                const next = (site.businessModels || []).filter((_, j) => j !== i);
                setSite((s) => ({ ...s, businessModels: next }));
              }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-3`}>
        <h2 className={`text-base font-semibold tracking-tight ${baseText}`}>About page — pricing</h2>
        <p className={`text-sm leading-relaxed ${mutedText}`}>
          The public About page no longer shows plan cards or checkout. It displays a single message that{" "}
          <span className={`font-semibold ${baseText}`}>all Mintly features are free</span>. Legacy pricing fields may still exist in stored site
          data but are not surfaced in the UI.
        </p>
      </div>

      <div className={`${cardBase} rounded-2xl p-5 sm:p-6 space-y-4`}>
        <div className="flex items-center justify-between gap-4">
          <h2 className={`text-base font-semibold tracking-tight ${baseText}`}>FAQ</h2>
          <button
            type="button"
            onClick={() =>
              setSite((s) => ({
                ...s,
                faqs: [...(s.faqs || []), { question: "", answer: "", order: (s.faqs || []).length }],
              }))
            }
            className="text-sm font-semibold text-mint"
          >
            + Add FAQ
          </button>
        </div>
        <label className={labelCls}>
          Section title
          <input className={inputCls} value={site.faqTitle || ""} onChange={(e) => setSite((s) => ({ ...s, faqTitle: e.target.value }))} />
        </label>
        {(site.faqs || []).map((row, i) => (
          <div key={i} className={`p-4 rounded-xl ${theme.inset}`}>
            <input
              className={inputCls}
              placeholder="Question"
              value={row.question}
              onChange={(e) => {
                const next = [...(site.faqs || [])];
                next[i] = { ...next[i], question: e.target.value };
                setSite((s) => ({ ...s, faqs: next }));
              }}
            />
            <textarea
              rows={3}
              placeholder="Answer"
              className={`${inputCls} mt-2`}
              value={row.answer}
              onChange={(e) => {
                const next = [...(site.faqs || [])];
                next[i] = { ...next[i], answer: e.target.value };
                setSite((s) => ({ ...s, faqs: next }));
              }}
            />
            <button
              type="button"
              className="mt-2 text-xs text-rose-400"
              onClick={() => {
                const next = (site.faqs || []).filter((_, j) => j !== i);
                setSite((s) => ({ ...s, faqs: next }));
              }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <button type="submit" className={`${theme.btnPrimary} px-6 py-2.5`}>
        Save landing page
      </button>
    </form>
  );
};

export default AdminLandingTab;
